/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {Button, Menu} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {MaterialMoreVertIcon} from '@ion/dls/src/icons/MaterialMoreVertIcon';
import type {Dashboard} from './DashboardList';

/**
 * Props for the ScheduledReportMenu component.
 */
export interface ScheduledReportMenuProps {
  dashboard: Dashboard;
  onClose: () => void;
  onOpen: (dashboardOid: string) => void;
  onScheduleReport: (dashboard: Dashboard) => void;
}

const useStyles = makeStyles(() => ({
  menuWrapper : {
    display : 'flex'
  },
  cellButtonRoot : {
    height    : '40px',
    marginTop : '-2px',
    minWidth  : 'unset !important',
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  },
  menuList : {
    paddingBottom : 0
  }
}));

/**
 * ScheduledReportMenu renders the row action menu for scheduling a dashboard
 * report from the analytics side panel. The menu opens to the right of the
 * three-dot action and stops click events from selecting the dashboard row.
 *
 * ```typescript
 * <ScheduledReportMenu dashboard={dashboard}
 *   onClose={() => setOpenMenuDashboardOid(null)}
 *   onOpen={setOpenMenuDashboardOid}
 *   onScheduleReport={scheduleReport} />
 * ```
 */
export const ScheduledReportMenu = ({
  dashboard,
  onClose,
  onOpen,
  onScheduleReport
}: ScheduledReportMenuProps) => {
  const classes = useStyles();
  const [anchorMenuEl, setAnchorMenuEl] = useState<HTMLElement | null>(null);

  const closeMenu = function(event?) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    setAnchorMenuEl(null);
    onClose();
  };

  const onMenuClick = function() {
    onScheduleReport(dashboard);
    closeMenu();
  };

  return (
    <span className={classes.menuWrapper}
      onClick={function(e) {
        e.preventDefault();
        e.stopPropagation();
      }}>
      <Button onClick={function(e) {
        e.preventDefault();
        e.stopPropagation();
        setAnchorMenuEl(e.currentTarget);
        onOpen(dashboard?.oid);
      }}
      className={classes.cellButtonRoot}
      disableRipple>
        <MaterialMoreVertIcon />
      </Button>
      <Menu anchorEl={anchorMenuEl}
        getContentAnchorEl={null}
        keepMounted
        anchorOrigin={{
          vertical   : 'center',
          horizontal : 'right'
        }}
        transformOrigin={{
          vertical   : 'top',
          horizontal : 'left'
        }}
        open={Boolean(anchorMenuEl)}
        MenuListProps={{
          className : classes.menuList
        }}
        onClose={closeMenu}>
        <CellMenuItem onClick={onMenuClick}
          label={translate('sisense:formLabels.scheduleReport')} />
      </Menu>
    </span>
  );
};

export default ScheduledReportMenu;
